import { codeInputCommonAttrs } from '../../../common/$models'
import { FindFilter, ModelDef } from '../../../types'
import { UserConditionsFilterConditionGroup } from '../../UserConditionsFilter/models/userConditionsFilterColumnsPartial'

const FilterComponentBasedOnTargetModel = {
  template:
    '<FilterResultDisplayContainer :emitValueAsObject="true" v-bind="attrsMerged"/>',
  computed: {
    collectionName() {
      // @ts-ignore
      return this.$attrs.recordRoot?.targetModelName
    },
    attrsMerged() {
      return {
        // @ts-ignore
        ...this.$attrs,
        collectionName: this.collectionName,
      }
    },
  },
}

const generateNodeKeysSelections = (
  recordRoot: ModelRecordWorkflowDefinitions,
  excludeKeys: string | string[] = [],
) => {
  if (typeof excludeKeys === 'string') {
    excludeKeys = [excludeKeys]
  }
  if (!Array.isArray(excludeKeys)) {
    excludeKeys = []
  }
  return recordRoot.routeNodeDefinitions
    ?.filter((node) => !excludeKeys.includes(node.key))
    .map((node) => ({
      value: node.key,
      label: `${node.title}${node.key !== node.title ? ` (${node.key})` : ''}`,
    }))
}

/**
 * ワークフロー定義のモデル
 * - レコードベースのワークフローの定義を管理
 * - ワークフローの開始条件、ルート定義、UI設定などを含む
 */
const model: ModelDef = {
  tableName: 'record_workflow_definitions',
  tableLabel: 'ワークフロー定義',
  primaryKeyColType: 'UUID',
  bulkControllable: true,
  extensions: [
    {
      enabled: true,
      extensionType: 'cachedFindable',
      key: 'cachedFindable',
    },
  ],
  formColumnGroups: {
    // basic: {
    //   type: 'tab',
    //   groupKey: 'tab1',
    //   label: '基本設定',
    //   order: 10,
    // },
    route: {
      type: 'tab',
      groupKey: 'tab1',
      label: 'ルート・アクション定義',
      order: 20,
    },
    // TODO: 検討
    // trigger: {
    //   type: 'tab',
    //   groupKey: 'tab1',
    //   label: '開始トリガー設定',
    //   order: 30,
    // },
    ui: {
      type: 'tab',
      groupKey: 'tab1',
      label: 'UI設定',
      order: 40,
    },
  },
  columns: {
    id: {
      type: 'UUID',
      label: 'ID',
      visible: false,
    },
    name: {
      type: 'STRING',
      label: 'ワークフロー定義名',
      validate: { notEmpty: true },
      // unique: true,
      // groupKey: 'basic',
      // inputHelpText: 'ワークフロー定義を識別するための名前を入力してください',
      width: { xs: 48, sm: 20 },
    },
    description: {
      type: 'TEXT',
      label: '管理用メモ',
      // groupKey: 'basic',
      width: { xs: 48, sm: 28 },
      inputHelpText: 'ワークフローの目的や使用方法について説明を記載',
    },
    isEnabled: {
      type: 'BOOLEAN',
      label: '有効状態',
      defaultValue: true,
      width: { xs: 'max-content' },
    },
    targetModelName: {
      type: 'STRING',
      label: '対象モデル',
      validate: { notEmpty: true },
      strictSelections: true,
      editable: true,
      editableOnCreate: true,
      selections: () => {
        return $core.$modelsLoader.modelNames
      },
      customLabel: (val) => {
        return $core.$models[val]?.tableLabel
          ? `${$core.$models[val].tableLabel} (${val})`
          : val
      },
      width: { xs: 'max-content' },
    },
    bridgeRelationModelName: {
      type: 'STRING',
      label: '中間テーブル (オプション)',
      comment:
        'ワークフローと対象レコードの関係性を管理する中間テーブル名を指定可能です。この項目を設定した場合には、ワークフロー開始時に中間テーブルのレコードも作成され、元モデルから ワークフロースレッドの状態を含めた検索などを行うことができるようになります。中間テーブルは モデル定義から事前に作成してください。 <code>record_workflow_threads</code> と 対象モデル の 2つのモデルへの M2Oリレーションを持っている必要があります。',
      width: { xs: 48, sm: 'max-content' },
      strictSelections: true,
      dynamicSelections: true,
      enableIf: (record) => !!record.targetModelName,
      selections: (record) => {
        if (!record.targetModelName) return []
        const targetModel = $core.$models[record.targetModelName]
        if (!targetModel) return []

        // 対象モデルに関連する中間テーブルを抽出
        return Object.entries($core.$models)
          .filter(([_, model]) => {
            // M2Oリレーションを持つカラムを検索
            const hasTargetModelRelation = Object.values(model.columns).some(
              (col) =>
                col.type === 'RELATIONSHIP_MANY_TO_ONE' &&
                col.relationshipManyToOne?.collectionName === record.targetModelName,
            )
            const hasWorkflowThreadRelation = Object.values(model.columns).some(
              (col) =>
                col.type === 'RELATIONSHIP_MANY_TO_ONE' &&
                col.relationshipManyToOne?.collectionName === 'record_workflow_threads',
            )
            // 両方のリレーションを持つモデルのみを選択肢として表示
            return hasTargetModelRelation && hasWorkflowThreadRelation
          })
          .map(([modelName, model]) => ({
            value: modelName,
            label: `${model.tableLabel} (${modelName})`,
          }))
      },
      customLabel: (val) => {
        return $core.$models[val]?.tableLabel
          ? `${$core.$models[val].tableLabel} (${val})`
          : val
      },
    },
    behaviorType: {
      type: 'SELECT',
      // visible: false,
      label: 'ワークフロータイプ',
      defaultValue: 'route_based_workflow',
      selections: () => [
        { value: 'route_based_workflow', label: 'ルートベースワークフロー' },
        // { value: 'review_request', label: 'レビュー依頼' },
      ],
      validate: { notEmpty: true },
      // groupKey: 'basic',
      // inputHelpText: 'ワークフローの挙動タイプを選択してください',
    },
    allowMultipleWorkflowThread: {
      visible: false,
      type: 'BOOLEAN',
      label: '複数ワークフロースレッドの開始を許可',
      defaultValue: false,
      groupKey: 'trigger',
      inputHelpText:
        '1レコードに対して複数の同じワークフローのスレッドが立ち上がることを許可するかどうか',
    },

    // トリガー条件の設定
    startTriggerConditions: {
      visible: false,
      enableIf: (record) => !!record.targetModelName,
      type: 'ARRAY_OF_OBJECT',
      label: 'ワークフロー開始条件',
      groupKey: 'trigger',
      width: { xs: 48 },
      inputHelpText: 'ワークフローを開始する条件を設定します。複数の条件を設定可能です。',
      columns: {
        triggerType: {
          type: 'SELECT',
          label: 'トリガータイプ',
          inputComponent: 'RadioSelectInput',
          selections: () => [
            { value: 'manual', text: '手動で開始' },
            { value: 'autoStart', text: '自動で開始' },
          ],
        },
        recordFilterCondition: {
          type: 'JSON',
          label: 'レコード条件',
          inputComponent: FilterComponentBasedOnTargetModel,
          validate: { notEmpty: true },
          inputHelpText: 'ワークフローを開始するレコードの条件を設定してください',
        },
        submitterUserCondition: {
          type: 'JSON',
          label: '開始可能ユーザ条件',
          inputComponent: {
            template: `<UserConditionsFilterWithModalInput v-bind="binds" />`,
            props: ['recordRoot'],
            computed: {
              binds() {
                return {
                  ...this.$attrs,
                  targetModelNames: [
                    'record_workflow_threads',
                    this.recordRoot.targetModelName,
                  ],
                }
              },
            },
          },
          validate: { notEmpty: true },
          inputHelpText: 'ワークフローを開始できるユーザの条件を設定してください',
        },
      },
    },

    // ルート定義
    routeNodeDefinitions: {
      type: 'ARRAY_OF_OBJECT',
      label: 'ノード & アクション (ルート) 定義',
      hideLabel: true,
      groupKey: 'route',
      width: { xs: 48 },
      enableIf: (row) => !!row.targetModelName,
      beforeComponents: [
        {
          template:
            '<div class="small"><HelpDoc>ワークフローにおける ノード (状態) と アクション を定義し、ワークフローのルートを構築します。<br/>1つのノード (例: "部長承認") に対して、複数のアクション (例: "承認", "差し戻し") および アクション実行後の遷移先ノードを定義します。</HelpDoc></div>',
        },
      ],
      columns: {
        key: {
          type: 'STRING',
          label: 'ノードキー',
          validate: { notEmpty: true },
          // inputHelpText: 'ルートノードを識別するための一意のキーを入力してください',
          width: { xs: 'max-content' },
        },
        title: {
          type: 'STRING',
          label: 'ノード 表示用タイトル',
          validate: { notEmpty: true },
          // inputHelpText: 'ノードのタイトルを入力してください',
          width: { xs: 'max-content' },
        },
        type: {
          type: 'SELECT',
          label: 'ノードタイプ',
          defaultValue: 'action',
          selections: () => [
            { value: 'action', label: '手動でのアクション' },
            // { value: 'automation', label: '自動処理' },
            { value: 'completed', label: '完了' },
            { value: 'canceled', label: 'キャンセル' },
          ],
          validate: { notEmpty: true },
          // inputHelpText: 'このルートでの処理タイプを選択してください',
          width: { xs: 'max-content' },
        },
        actions: {
          type: 'ARRAY_OF_OBJECT',
          label: 'アクション定義',
          width: { xs: 48 },
          minValueLength: 0,
          // enableIf: (row) => row.type === 'action',
          displayAsDetail: true,
          defaultValue: () => {
            // "承認" と "差し戻し" の 一般的 values を 返却する
            return $core.$configVars.get('record_workflow_definition.default_actions', [
              {
                key: '承認',
                actionLabel: '承認',
                markThisNextNodeAsDefaultRouting: true,
                isCommentRequired: true,
                buttonStyle: 'success',
                buttonIcon: 'check',
              },
              {
                key: '差し戻し',
                actionLabel: '差し戻し',
                markThisNextNodeAsDefaultRouting: false,
                isCommentRequired: true,
                buttonStyle: 'danger',
                buttonIcon: 'x',
              },
            ])
          },
          inputHelpText:
            'このノードで実行可能なアクションを定義します。各アクションには次のノードへの遷移設定も含めることができます。',
          columns: {
            key: {
              type: 'STRING',
              label: 'アクションキー',
              validate: { notEmpty: true },
              width: { xs: 'max-content' },
            },
            actionLabel: {
              type: 'STRING',
              label: 'アクションラベル',
              validate: { notEmpty: true },
              width: { xs: 'max-content' },
            },
            routing_defaultTargetNodeKeys: {
              type: 'MULTISELECT',
              label: 'デフォルトの遷移先ノード',
              validate: {
                notEmpty: true,
              },
              dynamicSelections: true,
              selections: (record, currentValue, initialValue, recordRoot) => {
                return generateNodeKeysSelections(
                  recordRoot as ModelRecordWorkflowDefinitions,
                  currentValue,
                )
              },
              width: { xs: 'max-content' },
              inputHelpText: 'このアクション実行後の遷移先ノード',
              editCallback: ({ row, newValue, oldValue }) => {
                console.log('editCallback', { row, newValue, oldValue })
                if (newValue?.length > 1) {
                  row.routing_defaultTargetNodeKeys = [newValue[1]]
                }
                return row
              },
            },
            markThisNextNodeAsDefaultRouting: {
              type: 'BOOLEAN',
              label: 'このアクションを正常系ルートとして設定',
              defaultValue: false,
              inputHelpText: 'ルートを算出に関連します',
            },

            isCommentRequired: {
              beforeComponents: [
                {
                  template: '<h5>UI・スタイル設定</h5>',
                },
              ],
              type: 'BOOLEAN',
              label: 'コメント入力必須',
              defaultValue: true,
              displayAsDetail: true,
              width: { xs: 'max-content' },
            },
            // アクションボタンのスタイル設定
            buttonStyle: {
              displayAsDetail: true,
              type: 'SELECT',
              label: 'ボタンスタイル',
              selections: () => [
                { value: 'primary', label: 'primary' },
                { value: 'secondary', label: 'secondary' },
                { value: 'success', label: 'success' },
                { value: 'danger', label: 'danger' },
                { value: 'warning', label: 'warning' },
                { value: 'info', label: 'info' },
                { value: 'outline-primary', label: 'outline-primary' },
                { value: 'outline-secondary', label: 'outline-secondary' },
                { value: 'outline-success', label: 'outline-success' },
                { value: 'outline-danger', label: 'outline-danger' },
                { value: 'outline-warning', label: 'outline-warning' },
                { value: 'outline-info', label: 'outline-info' },
              ],
              defaultValue: 'primary',
              width: { xs: 'max-content' },
            },
            buttonIcon: {
              displayAsDetail: true,
              type: 'STRING',
              label: 'ボタンアイコン',
              inputComponent: 'IconSelectInput',
              width: { xs: 'max-content' },
            },
            enableIfActionUserCondition: {
              beforeComponents: [
                {
                  template: '<h5>アクションが実行できる条件</h5>',
                },
              ],
              type: 'JSON',
              label: 'アクション実行可能ユーザ条件',
              // inputComponent: FilterComponentBasedOnUserModel,
              inputComponent: {
                // TODO: 一旦, ネストレベルを 1 にしているが 今後 算出を強化する (そのためには 算出の際に渡しているレコード を nest 条件に合わせて 再fetch する必要がある)
                template: `<UserConditionsFilterWithModalInput v-bind="binds" :max-nested-column-level="1" />`,
                props: ['recordRoot'],
                computed: {
                  binds() {
                    return {
                      ...this.$attrs,
                      targetModelNames: [
                        'record_workflow_threads',
                        this.recordRoot.targetModelName,
                      ],
                    }
                  },
                },
              },
              width: { xs: 'max-content' },
              displayAsDetail: true,
            },
            // enableIfActionUserConditionAdditionals: {
            //   displayAsDetail: true,
            //   type: 'MULTISELECT',
            //   // inputComponent: 'MultiselectCheckboxesInput',
            //   label: 'アクション実行可能ユーザ条件の追加条件',
            //   selections: () => [
            //     {
            //       value: 'is_workflow_start_user',
            //       label: 'ワークフローを開始したユーザ',
            //     },
            //     {
            //       value: 'belong_in_the_same_or_upper_org_as_workflow_start_user',
            //       label: 'ワークフローを開始したユーザと同じ組織 または 上位組織に所属',
            //     },
            //   ],
            //   width: { xs: 'max-content' },
            //   defaultValue: ['belong_in_the_same_or_upper_org_as_workflow_start_user'],
            // },
            enableIfRecordCondition: {
              displayAsDetail: true,
              type: 'JSON',
              label: 'アクション実行可能レコード条件',
              inputComponent: FilterComponentBasedOnTargetModel,
              width: { xs: 'max-content' },
            },
            enableIfNodeConditionNodeKeys: {
              displayAsDetail: true,
              type: 'MULTISELECT',
              label: 'アクション実行可能ノード条件',
              // inputComponent: FilterComponentBasedOnTargetModel,
              // width: { xs: 'max-content' },
              dynamicSelections: true,
              selections: (record, currentValue, initialValue, recordRoot) => {
                return generateNodeKeysSelections(
                  recordRoot as ModelRecordWorkflowDefinitions,
                )
              },
              inputHelpText:
                '設定しない場合は "現在のノード" の状態にある場合にのみ アクションが実行可能となります。',
            },
            useRoutingNextNodeResolver: {
              beforeComponents: [
                {
                  template: '<h5>遷移先ノード条件</h5>',
                },
              ],
              displayAsDetail: true,
              type: 'BOOLEAN',
              label: '遷移先ノードを決定する関数を利用',
              defaultValue: false,
              width: { xs: 'max-content' },
            },
            // アクション定義内のルーティング関連の設定
            routing_nextNodeResolver: {
              displayAsDetail: true,
              type: 'TEXT',
              label: '遷移先ノードを決定する関数',
              enableIf: (row) => row.useRoutingNextNodeResolver,
              inputComponent: 'CodeEditor',
              width: { xs: 48 },
              inputAttrs: {
                ...codeInputCommonAttrs,
                initCode: `/**
 * @param {Object} context - ルーティング判定に必要なコンテキスト
 * @param {Record<string, any>} context.record - 現在のレコード
 * @param {Record<string, any>} context.currentNode - 現在のノード
 * @param {Record<string, any>} context.action - 実行されたアクション
 * @param {Record<string, any>[]} context.actionHistory - このノードでの全アクション実行履歴
 *   - userId: アクションを実行したユーザID
 *   - actionKey: 実行されたアクションのキー
 *   - executedAt: 実行日時
 *   - comment: コメント
 * @param {Record<string, any>[]} context.workflowHistory - ワークフロー全体の実行履歴
 * @param {Function} context.getUserInfo - ユーザ情報を取得する関数
 *   - 使用例: const user = await getUserInfo(userId)
 *   - 返却値: { id, email, position, department, roles, ... }
 * @returns {Promise<string>} - 次のノードのキーを返却
 */
return async ({ record, currentNode, action, actionHistory, workflowHistory, getUserInfo }) => {
  // // 例1: シンプルな条件分岐
  // if (action.key === 'reject') {
  //   return ['revision_node']
  // }
  
  // // 例2: 承認金額による条件分岐
  // if (record.amount > 1000000) {
  //   return 'special_approval_node'
  // }
  
  // // 例3: 部長の承認が2名以上必要
  // const directorApprovals = await Promise.all(
  //   actionHistory
  //     .filter(h => h.actionKey === 'approve')
  //     .map(async h => {
  //       const user = await getUserInfo(h.userId)
  //       return user.position === 'director'
  //     })
  // )
  // if (directorApprovals.filter(Boolean).length < 2) {
  //   return currentNode.key // まだ条件を満たしていないので同じノードに留まる
  // }
  
  // // 例4: 全ての課長の承認が必要
  // const managers = await Promise.all(
  //   (await context.$core.$models.directus_users.findAllBy({
  //     position: { _eq: 'manager' }
  //   })).map(u => u.id)
  // )
  // const managerApprovals = actionHistory
  //   .filter(h => h.actionKey === 'approve')
  //   .map(h => h.userId)
  // const allManagersApproved = managers.every(id => managerApprovals.includes(id))
  // if (!allManagersApproved) {
  //   return currentNode.key
  // }
  
  // // 例5: 承認履歴に基づく条件分岐
  // const previousNode = workflowHistory[workflowHistory.length - 2]
  // if (previousNode?.key === 'special_review' && action.key === 'approve') {
  //   return 'final_approval_node'
  // }
  
  // デフォルトの遷移先 の キーを返却
  return ['next_approval_node']
}`,
              },
              inputHelpText: `
次のノードを決定する関数を実装してください。<br/>
- 同じノードのキーを返却すると、そのノードに留まります<br/>
- アクション履歴を使用して、必要な承認回数などを判定できます<br/>
- ユーザ情報を使用して、役職や部署による条件分岐が可能です<br/>
- ワークフロー履歴を使用して、過去の状態に基づく判定が可能です
  `.trim(),
            },
            routing_useConditionalRouting: {
              displayAsDetail: true,
              type: 'BOOLEAN',
              label: '条件付き遷移を利用',
              defaultValue: false,
              width: { xs: 'max-content' },
            },
            routing_conditionalRoutes: {
              displayAsDetail: true,
              type: 'ARRAY_OF_OBJECT',
              label: '条件付き遷移設定',
              width: { xs: 48 },
              enableIf: (row) => row.routing_useConditionalRouting,
              columns: {
                targetNodeKeys: {
                  type: 'MULTISELECT',
                  label: '遷移先ノード',
                  validate: { notEmpty: true },
                  dynamicSelections: true,
                  selections: (record, currentValue, initialValue, recordRoot) => {
                    return (
                      recordRoot.routeNodeDefinitions?.map((node) => ({
                        value: node.key,
                        label: `${node.title} (${node.key})`,
                      })) || []
                    )
                  },
                  width: { xs: 'max-content' },
                  inputHelpText: '条件に合致した場合の遷移先ノード',
                  editCallback: ({ row, newValue, oldValue }) => {
                    console.log('editCallback', { row, newValue, oldValue })
                    if (newValue?.length > 1) {
                      row.targetNodeKeys = [newValue[1]]
                    }
                    return row
                  },
                },
                condition: {
                  type: 'JSON',
                  label: '遷移条件',
                  inputComponent: FilterComponentBasedOnTargetModel,
                  inputHelpText:
                    'この条件に合致した場合に、デフォルトの遷移先ではなくこのノードに遷移します',
                  width: { xs: 'max-content' },
                  enableIf: (row) => !row.useConditionAsFunction,
                },
                useConditionAsFunction: {
                  type: 'BOOLEAN',
                  label: '関数による条件設定',
                  defaultValue: false,
                  width: { xs: 'max-content' },
                },
                conditionAsFunction: {
                  type: 'TEXT',
                  label: '条件関数',
                  enableIf: (row) => row.useConditionAsFunction,
                  inputComponent: 'CodeEditor',
                  inputAttrs: {
                    ...codeInputCommonAttrs,
                    initCode: `/**
 * 指定された 遷移先ノード へ 遷移するかどうかを判定
 * @param {Object} args
 * @param {Record<string, any>} args.record - 現在のレコード
 * @param {Record<string, any>} args.action - 実行されたアクション
 * @param {Record<string, any>} args.workflowContext - ワークフローのコンテキスト
 * @returns {Promise<boolean>}
 */
return async ({record, action, workflowContext}) => {
  // 現在のレコード 等 から 条件を判定, true / false を返却
  // return record.total_amount >= 10000 * 10 // 10万円を超える場合は 指定されたNodeに遷移
  return false
}`,
                  },
                  width: { xs: 48 },
                },
              },
            },

            // アクション実行前のコールバック
            useBeforeExecuteCallback: {
              beforeComponents: [
                {
                  template: '<h5>アクション実行前/後の 追加処理</h5>',
                },
              ],
              displayAsDetail: true,
              type: 'BOOLEAN',
              label: 'アクション実行前の処理を利用',
              inputHelpText:
                'アクション実行前に実行される処理を追記可能です。複雑な条件による、アクション実行のキャンセルを行いたい場合にも利用可能です。',
              defaultValue: false,
              width: { xs: 48 },
            },
            beforeExecuteCallback: {
              displayAsDetail: true,
              type: 'TEXT',
              label: 'アクション実行前の処理',
              enableIf: (row) => row.useBeforeExecuteCallback,
              inputComponent: 'CodeEditor',
              width: { xs: 48 },
              inputAttrs: {
                ...codeInputCommonAttrs,
                initCode: `/**
 * @param {Object} context - 実行コンテキスト
 * @param {Record<string, any>} context.record - 現在のレコード
 * @param {Record<string, any>} context.node - 現在のノード
 * @param {Record<string, any>} context.action - 実行されるアクション
 * @param {Object} context.service - ワークフローサービス
 * @returns {Promise<boolean>} - falseを返すとアクションを実行しない
 */
return async ({ record, node, action, service }) => {
  // 処理を記述
  return true
}`,
              },
            },

            // アクション実行後のコールバック
            useAfterExecuteCallback: {
              displayAsDetail: true,
              type: 'BOOLEAN',
              label: 'アクション実行後の処理を利用',
              inputHelpText: 'アクション完了直後に実行される処理を追記可能です。',
              defaultValue: false,
              width: { xs: 48 },
            },
            afterExecuteCallback: {
              displayAsDetail: true,
              type: 'TEXT',
              label: 'アクション実行後の処理',
              enableIf: (row) => row.useAfterExecuteCallback,
              inputComponent: 'CodeEditor',
              width: { xs: 48 },
              inputAttrs: {
                ...codeInputCommonAttrs,
                initCode: `/**
 * @param {Object} context - 実行コンテキスト
 * @param {Record<string, any>} context.record - 現在のレコード
 * @param {Record<string, any>} context.node - 現在のノード
 * @param {Record<string, any>} context.action - 実行されたアクション
 * @param {Object} context.service - ワークフローサービス
 * @returns {Promise<void>}
 */
return async ({ record, node, action, service }) => {
  // 処理を記述
}`,
              },
            },

            // レコード値の更新設定
            useUpdateRecordValues: {
              displayAsDetail: true,
              type: 'BOOLEAN',
              label: 'アクション完了時のレコード値更新を利用',
              defaultValue: false,
              width: { xs: 48 },
              inputHelpText:
                'アクション完了後に、レコードの特定のカラムの値を更新する場合に利用します。',
            },
            updateRecordValues: {
              displayAsDetail: true,
              type: 'ARRAY_OF_OBJECT',
              label: 'レコード値の更新設定',
              width: { xs: 48 },
              enableIf: (row) => row.useUpdateRecordValues,
              columns: {
                useFunction: {
                  type: 'BOOLEAN',
                  label: '関数で設定',
                  defaultValue: false,
                  width: { xs: 'max-content' },
                },
                function: {
                  type: 'TEXT',
                  label: 'レコード値の更新算出関数',
                  enableIf: (row) => !!row.useFunction,
                  inputComponent: 'CodeEditor',
                  width: { xs: 48 },
                  defaultValue: '',
                  inputAttrs: {
                    initCode: ($inputVm) => {
                      const targetModelName = $inputVm?.recordRoot?.targetModelName
                      return `/**
                 * @param {Partial<ModelDataTypes['${targetModelName}']>} updateRecordValues - 更新対象のレコード値
                 * @param {Object} context - 実行コンテキスト
                 * @param {ModelDataTypes['${targetModelName}']} context.record - 現在のレコードデータ
                 * @param {import("plugins/RecordWorkflow/front/services/WorkflowNodeAction").WorkflowNodeAction} context.action - 実行されたアクション
                 * @param {Record<string, any>} context.workflowContext - ワークフローのコンテキスト
                 * @returns {Promise<any>}
                 */
                return async (updateRecordValues, {record, action, workflowContext}) => {
                  // 更新したい props を updateRecordValues に記述します
                  // updateRecordValues.someColName = record.amount >= 1000000 ? '特別承認' : '承認'
                  return updateRecordValues
                }`
                    },
                  },
                },
                columnName: {
                  type: 'STRING',
                  label: '更新対象カラム',
                  validate: { notEmpty: true },
                  dynamicSelections: true,
                  selections: (record, currentValue, initialValue, recordRoot) => {
                    return recordRoot.targetModelName
                      ? $core.$models[recordRoot.targetModelName].colNames
                      : []
                  },
                  customLabel: (val, callerVueInstance, recordRoot) => {
                    // model の label を 返却する
                    const label =
                      $core.$models[recordRoot.targetModelName].columns[val].label
                    return `${label} (${val})`
                  },
                  enableIf: (row) => !row.useFunction,
                  defaultValue: '',
                },
                value: {
                  type: 'STRING',
                  label: '設定値',
                  validate: { notEmpty: true },
                  enableIf: (row) => !row.useFunction,
                  defaultValue: '',
                },
              },
            },
          },
        },
      },
    },

    // UI設定
    workflowUiConfig: {
      type: 'ARRAY_OF_OBJECT',
      label: 'フォーム内 表示設定',
      hideLabel: true,
      maxValueLength: 1,
      minValueLength: 1,
      groupKey: 'ui',
      width: { xs: 48 },
      beforeComponents: [
        `<div class="small">
          <HelpDoc>
            ワークフローの表示方法や操作方法をカスタマイズできます。
            <ul>
              <li>ワークフロー開始ボタン: ラベル、スタイル、位置などを設定</li>
              <li>ワークフロー状態表示: 表示スタイル、位置、履歴表示などを設定</li>
              <li>アクションボタン: 各アクションのスタイル、表示方法をカスタマイズ</li>
            </ul>
            より高度なカスタマイズが必要な場合は、カスタムコンポーネントとして実装することも可能です。 (<code v-pre>&lt;WorkflowStartButtonWithWorkflowDefinitionId workflow-definition-id="xxxx-xxxxx-xxxxx" /&gt;</code>)
          </HelpDoc>
        </div>`,
      ],
      columns: {
        // 一括操作を有効化
        enableBulkAction: {
          type: 'BOOLEAN',
          label: '一括操作を有効化',
          defaultValue: true,
          width: { xs: 'max-content' },
          inputHelpText:
            'データ一覧表示時に チェックを入れたデータに対して 一括アクションを実行できるようになります。',
        },
        // ワークフロー開始ボタンの設定
        startButtonConfig: {
          type: 'ARRAY_OF_OBJECT',
          label: 'ワークフロー開始ボタンの設定',
          width: { xs: 48 },
          maxValueLength: 1,
          minValueLength: 1,
          columns: {
            position: {
              type: 'SELECT',
              label: '表示位置',
              selections: () => [
                { value: 'hidden', label: '非表示' },
                { value: 'form_top', label: 'フォーム上部' },
                { value: 'form_bottom', label: 'フォーム下部' },
                { value: 'form_submit_button_left', label: 'フォーム送信ボタン 左横' },
                { value: 'form_submit_button_right', label: 'フォーム送信ボタン 右横' },
                { value: 'column_before', label: '特定カラム前' },
                { value: 'column_after', label: '特定カラム後' },
              ],
              defaultValue: 'form_top',
            },
            targetColumnName: {
              type: 'STRING',
              label: '表示対象カラム',
              enableIf: (row) => ['column_after', 'column_before'].includes(row.position),
              dynamicSelections: true,
              selections: (record, currentValue, initialValue, recordRoot) => {
                return recordRoot.targetModelName
                  ? $core.$models[recordRoot.targetModelName].colNames
                  : []
              },
              customLabel: (val, callerVueInstance, recordRoot) => {
                // model の label を 返却する
                const label =
                  $core.$models[recordRoot.targetModelName]?.columns[val]?.label
                return label ? `${label} (${val})` : val
              },
            },
            label: {
              type: 'STRING',
              label: 'ボタンラベル',
              defaultValue: 'ワークフローを開始',
              inputHelpText: 'ワークフロー開始ボタンに表示するテキスト',
            },
            style: {
              type: 'STRING',
              label: 'スタイル',
              selections: () => [
                { value: 'primary', label: 'primary' },
                { value: 'outline-primary', label: 'outline-primary' },
                { value: 'secondary', label: 'secondary' },
                { value: 'outline-secondary', label: 'outline-secondary' },
                { value: 'success', label: 'success' },
                { value: 'outline-success', label: 'outline-success' },
                { value: 'danger', label: 'danger' },
                { value: 'outline-danger', label: 'outline-danger' },
                { value: 'warning', label: 'warning' },
                { value: 'outline-warning', label: 'outline-warning' },
                { value: 'info', label: 'info' },
                { value: 'outline-info', label: 'outline-info' },
              ],
              defaultValue: 'primary',
              inputAttrs: {
                wrapperStyle: {
                  maxWidth: '160px',
                },
              },
            },
            icon: {
              type: 'STRING',
              label: 'アイコン',
              inputComponent: 'IconSelectInput',
              defaultValue: 'play-circle',
            },
            actionableRecordConditions: {
              type: 'JSON',
              label: 'ワークフロー開始実行可能レコード条件',
              width: { xs: 'max-content' },
              // defaultValue: false,
              inputComponent: FilterComponentBasedOnTargetModel,
            },
            actionableUserConditions: {
              type: 'JSON',
              label: 'ワークフロー開始実行可能ユーザ条件',
              width: { xs: 'max-content' },
              // defaultValue: false,
              inputComponent: {
                template: `<UserConditionsFilterWithModalInput v-bind="binds" />`,
                props: ['recordRoot'],
                computed: {
                  binds() {
                    return {
                      ...this.$attrs,
                      targetModelNames: [
                        // 'record_workflow_threads',
                        this.recordRoot.targetModelName,
                      ],
                    }
                  },
                },
              },
            },
          },
        },

        // ワークフロー状態表示の設定
        statusDisplayConfig: {
          type: 'ARRAY_OF_OBJECT',
          label: 'ワークフロー状態表示の設定',
          width: { xs: 48 },
          maxValueLength: 1,
          minValueLength: 1,
          columns: {
            displayStyle: {
              type: 'SELECT',
              label: '表示スタイル',
              selections: () => [
                { value: 'vertical', label: '縦並び ステップリスト' },
                // { value: 'horizontal', label: '横並び タブ' },
                // { value: 'horizontal-steps', label: '横並び ステップリスト' },
              ],
              defaultValue: 'vertical',
              inputHelpText: 'ワークフローの進行状況の表示方法を選択してください',
            },
            position: {
              type: 'SELECT',
              label: '表示位置',
              selections: () => [
                { value: 'hidden', label: '非表示' },
                { value: 'form_top', label: 'フォーム上部' },
                { value: 'form_bottom', label: 'フォーム下部' },
                { value: 'column_before', label: '特定カラム前' },
                { value: 'column_after', label: '特定カラム後' },
              ],
              defaultValue: 'form_top',
            },
            targetColumnName: {
              type: 'STRING',
              label: '表示対象カラム',
              enableIf: (row) => ['column_after', 'column_before'].includes(row.position),
              dynamicSelections: true,
              selections: (record, currentValue, initialValue, recordRoot) => {
                return recordRoot.targetModelName
                  ? $core.$models[recordRoot.targetModelName].colNames
                  : []
              },
              customLabel: (val, callerVueInstance, recordRoot) => {
                // model の label を 返却する
                const label =
                  $core.$models[recordRoot.targetModelName]?.columns[val]?.label
                return label ? `${label} (${val})` : val
              },
              inputHelpText: 'カスタム位置の場合、このカラムの前後に表示されます',
            },
            positionRelative: {
              type: 'SELECT',
              label: '相対位置',
              enableIf: (row) => row.position === 'custom',
              selections: () => [
                { value: 'before', label: '前' },
                { value: 'after', label: '後' },
              ],
              defaultValue: 'before',
            },
            visibleUserConditions: {
              type: 'JSON',
              label: '参照可能ユーザ条件',
              width: { xs: 'max-content' },
              // defaultValue: false,
              inputComponent: {
                template: `<UserConditionsFilterWithModalInput v-bind="binds" />`,
                props: ['recordRoot'],
                computed: {
                  binds() {
                    return {
                      ...this.$attrs,
                      targetModelNames: [
                        'record_workflow_threads',
                        this.recordRoot.targetModelName,
                      ],
                    }
                  },
                },
              },
            },
            // showHistory: {
            //   type: 'BOOLEAN',
            //   label: '履歴を表示',
            //   defaultValue: true,
            //   inputHelpText: '各ステップでの変更履歴を表示するかどうか',
            // },
            // showComments: {
            //   type: 'BOOLEAN',
            //   label: 'コメントを表示',
            //   defaultValue: true,
            //   inputHelpText: '各ステップでのコメントを表示するかどうか',
            // },
            // showAssignees: {
            //   type: 'BOOLEAN',
            //   label: '担当者を表示',
            //   defaultValue: true,
            //   inputHelpText: '各ステップの担当者情報を表示するかどうか',
            // },
            // mobileDisplayStyle: {
            //   type: 'SELECT',
            //   label: 'モバイル表示スタイル',
            //   selections: () => [
            //     { value: 'full', label: '通常' },
            //     { value: 'compact', label: 'コンパクト' },
            //     { value: 'minimal', label: '最小限' },
            //   ],
            //   defaultValue: 'full',
            //   inputHelpText: 'モバイル端末での表示方法を選択してください',
            // },
          },
        },
      },
    },
  },
  beforeSave: async (record: ModelRecordWorkflowDefinitions) => {
    // 特殊整形対応: targetModelName と userConditions の整形
    record.routeNodeDefinitions?.forEach((node) => {
      node.actions?.forEach((action) => {
        action.enableIfActionUserCondition = action.enableIfActionUserCondition?.map(
          (condition) => {
            condition.targetModelNames = [
              record.targetModelName,
              'record_workflow_threads',
            ]
            return condition
          },
        )
      })
    })
    return record
  },
}

export const record_workflow_definitions = model

// アクション実行可能ユーザー条件の追加条件タイプ
export type WorkflowActionUserConditionAdditional =
  | 'is_workflow_start_user'
  | 'belong_in_the_same_or_upper_org_as_workflow_start_user'
  | string // 今後, 他の条件も追加可能

/**
 * # Model定義 "record_workflow_definitions" (ワークフロー定義)
 * @comment レコードベースのワークフローの定義を管理するモデル
 *
 * @primaryKeyColType UUID
 */
export interface ModelRecordWorkflowDefinitions {
  /**
   * @label ID
   */
  id: string

  /**
   * @label ワークフロー定義名
   */
  name: string

  /**
   * @label 管理用メモ
   */
  description: string

  /**
   * @label 有効状態
   */
  isEnabled: boolean

  /**
   * @label 対象モデル
   */
  targetModelName: string

  /**
   * @label 中間テーブル (オプション)
   * @comment ワークフローと対象レコードの関係性を管理する中間テーブル名
   */
  bridgeRelationModelName?: string

  /**
   * @label ワークフロータイプ
   */
  behaviorType: 'route_based_workflow' // | 'review_request' // 今後追加予定

  /**
   * @label 複数ワークフロースレッドの開始を許可
   */
  allowMultipleWorkflowThread: boolean

  /**
   * @label ワークフロー開始条件
   */
  startTriggerConditions: {
    /** トリガータイプ */
    triggerType: 'manual' | 'autoStart'
    /** レコード条件 */
    recordFilterCondition: Record<string, unknown>
    /** 送信者ユーザ条件 */
    submitterUserCondition: Record<string, unknown>
  }[]

  /**
   * @label ワークフローのルートノード定義
   */
  routeNodeDefinitions: {
    /** ノードを識別するためのキー */
    key: string
    /** ノードの表示名 */
    title: string
    /** ノードの種類 */
    type: 'action' | 'automation' | 'completed' | 'canceled'
    /** アクション定義 */
    actions: {
      /** アクションを識別するためのキー */
      key: string
      /** アクションの表示名 */
      actionLabel: string
      /** デフォルトの遷移先ノード */
      routing_defaultTargetNodeKeys: string[]
      /** このアクションを正常系ルートとして設定 */
      markThisNextNodeAsDefaultRouting: boolean

      /** 遷移先ノードを決定する関数を利用するかどうか */
      useRoutingNextNodeResolver: boolean
      /** 遷移先ノードを決定する関数 */
      routing_nextNodeResolver?: string

      /** 条件付き遷移を利用するかどうか */
      routing_useConditionalRouting: boolean
      /** 条件付き遷移の設定 */
      routing_conditionalRoutes?: {
        /** 遷移先ノード */
        targetNodeKeys: string[]
        /** 遷移条件 */
        condition: Record<string, unknown>
        /** 関数による条件設定を利用するかどうか */
        useConditionAsFunction: boolean
        /** 条件関数 */
        conditionAsFunction?: string
      }[]

      /** コメント入力必須かどうか */
      isCommentRequired: boolean

      /** アクション実行可能ユーザ条件 */
      enableIfActionUserCondition?: UserConditionsFilterConditionGroup[]
      /** アクション実行可能ユーザ条件の追加条件 */
      enableIfActionUserConditionAdditionals?: WorkflowActionUserConditionAdditional[]
      /** アクション実行可能レコード条件 */
      enableIfRecordCondition?: Record<string, unknown>
      /** アクション実行可能ノード条件 */
      enableIfNodeConditionNodeKeys?: string[]

      /** ボタンスタイル */
      buttonStyle?: string
      /** ボタンアイコン */
      buttonIcon?: string

      /** アクション実行前の処理を利用するかどうか */
      useBeforeExecuteCallback: boolean
      /** アクション実行前の処理 */
      beforeExecuteCallback?: string

      /** アクション実行後の処理を利用するかどうか */
      useAfterExecuteCallback: boolean
      /** アクション実行後の処理 */
      afterExecuteCallback?: string

      /** アクション完了時のレコード値更新を利用するかどうか */
      useUpdateRecordValues: boolean
      /** レコード値の更新設定 */
      updateRecordValues?: {
        /** 更新対象カラム */
        columnName: string
        /** 設定値 */
        value: string
        /** 関数で設定するかどうか */
        useFunction: boolean
        /** 関数 */
        function?: string
      }[]
    }[]
  }[]

  /**
   * @label フォーム内 表示設定
   */
  workflowUiConfig: {
    /** 一括操作を有効化 */
    enableBulkAction: boolean
    /** ワークフロー開始ボタンの設定 */
    startButtonConfig: [
      {
        /** 表示位置 */
        position:
          | 'hidden'
          | 'form_top'
          | 'form_bottom'
          | 'form_submit_button_left'
          | 'form_submit_button_right'
          | 'column_before'
          | 'column_after'
        /** 表示対象カラム (position が column_before または column_after の場合のみ) */
        targetColumnName?: string
        /** ボタンラベル */
        label: string
        /** スタイル */
        style: string
        /** アイコン */
        icon: string
        /** ワークフロー開始実行可能ユーザ条件 */
        actionableUserConditions?: UserConditionsFilterConditionGroup[]
        /** ワークフロー開始実行可能レコード条件 */
        actionableRecordConditions?: FindFilter
        /** ワークフロー開始確認メッセージ */
        confirmMessage?: string
      },
    ]
    /** ワークフロー状態表示の設定 */
    statusDisplayConfig: [
      {
        /** 表示スタイル */
        displayStyle: 'vertical' // | 'horizontal' | 'horizontal-steps' // 今後追加予定
        /** 表示位置 */
        position: 'hidden' | 'form_top' | 'form_bottom' | 'column_before' | 'column_after'
        /** 表示対象カラム (position が column_before または column_after の場合のみ) */
        targetColumnName?: string
        /** 参照可能ユーザ条件 */
        visibleUserConditions?: Record<string, unknown>
      },
    ]
  }[]

  /**
   * @label 作成日時
   */
  createdAt: string

  /**
   * @label 更新日時
   */
  updatedAt: string
}
